import React, { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import AILightComponent from '../../components/AILightComponent';
import SpeechInputComponent from '../../components/SpeechInputComponent';

const categories = [
  "Vegetables",
  "Fruits",
  "Spices",
  "Food Grains",
  "Poultry",
  "Dairy",
  "Fruit & Veg Mixed",
  "Spices & Food Grains Mixed",
];

const basketTypes = [
  "Current Need",
  "Fixed Amount",
  "Weekly Basket",
  "Monthly Basket",
];

const processingTypes = ["Raw", "Washed", "Cut"];
const amountOptions = Array.from({ length: 10 }, (_, i) => (i + 1) * 50);
const units = ["kg", "g", "litre", "ml", "pcs"];

const itemsList = [
  "Potato",
  "Onion",
  "Tomato",
  "Milk",
  "Eggs",
  "Chicken",
  "Rice",
  "Wheat",
  "Turmeric",
  "Chilli",
];

const demoOrder = {
  basketType: "Fixed Amount",
  category: "Vegetables",
  amount: 200,
  processing: "Washed",
  supplierCode: "SUP123",
  boutiqueCode: "B86561",
  reffererCode: "TU123456789012",
  resellerCode: "TU123456789012",
  couponCode: "C12345",
  groupCode: "CG1235467",
  items: {
    Potato: { mode: "qty", qty: 2, unit: "kg", instruction: "Medium size" },
    Tomato: { mode: "amount", amount: 50, instruction: "Ripe" },
  },
  finalRemarks: "Deliver before 6 PM",
};

const myOrders = [
  { id: "ORD001", date: "2026-03-25", supplier: "SUP123", amount: 250, status: "Delivered" },
  { id: "ORD002", date: "2026-03-26", supplier: "SUP456", amount: 400, status: "Pending" },
];

const customerOrders = [
  { id: "CUST001", date: "2026-03-25", customer: "C001", amount: 300, status: "Delivered" },
  { id: "CUST002", date: "2026-03-26", customer: "C002", amount: 150, status: "Pending" },
];

const BudgetBasket = () => {  
    const authState = useSelector((state) => state.authReducer || {});
    const isLoggedIn = authState?.isLoggedIn || false;
    const userName = authState?.user?.name || "User";

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const handleLogout = () => {
        dispatch(logout());
        navigate("/");
    };  
 
    const [basketType, setBasketType] = useState("");
    const [category, setCategory] = useState("");
    const [amount, setAmount] = useState("");
    const [processing, setProcessing] = useState("");
    const [supplierCode, setSupplierCode] = useState("");
    const [boutiqueCode, setBoutiqueCode] = useState("");
    const [reffererCode, setReffererCode] = useState("");
    const [resellerCode, setResellerCode] = useState("");
    const [couponCode, setCouponCode] = useState("");
    const [groupCode, setGroupCode] = useState("");

    const [selectedItems, setSelectedItems] = useState({});
    const [finalRemarks, setFinalRemarks] = useState("");
    const [voiceInput, setVoiceInput] = useState("");

    const [view, setView] = useState("form");
    const [repeatOrderId, setRepeatOrderId] = useState("");

    const [expandedCustomerOrder, setExpandedCustomerOrder] = useState(null);
    const [customerOrderFilter, setCustomerOrderFilter] = useState("all");
    const [customerSearchTerm, setCustomerSearchTerm] = useState("");
    const [customerStartDate, setCustomerStartDate] = useState("");
    const [customerEndDate, setCustomerEndDate] = useState("");
    const [customerSortBy, setCustomerSortBy] = useState("date-desc");
    const [customerCurrentPage, setCustomerCurrentPage] = useState(1);
    const customerItemsPerPage = 5;
    
    const [selectedLanguage, setSelectedLanguage] = useState("en");
    // AI Assistant States
    const [showAIResponse, setShowAIResponse] = useState(false);
    const [currentAIAction, setCurrentAIAction] = useState("");
    const [enableVoiceInput, setEnableVoiceInput] = useState(false);
    const [enableVoiceRecording, setEnableVoiceRecording] = useState(false);

    const handleAutoOrderGeneration = () => {
      setShowAIResponse(true);
      // Optional: trigger any pre-fill logic here if needed
    };

    const handleAIAction = (action) => {
      setCurrentAIAction(action);
      setShowAIResponse(true);

      let message = "";

      switch (action) {
        case "autoOrder":
          message = "Generating smart order based on your selected items and preferences...";
          break;
        case "voiceInput":
          message = "Voice input mode activated. Speak your requirements now...";
          break;
        case "recordSeller":
          message = "Recording voice message for the seller. Speak clearly...";
          break;
        default:
          message = "AI Assistant activated";
      }

      alert(`✅ ${message}\n\nAI Support panel opened below.`);
    };

    // Computed: Filter + Sort + Paginate Customer Orders
    const filteredCustomerOrders = customerOrders
      .filter((order) => {
        // Status filter
        if (customerOrderFilter !== "all" && order.status !== customerOrderFilter) return false;

        // Search term (Order ID or Customer ID)
        if (customerSearchTerm) {
          const term = customerSearchTerm.toLowerCase();
          return (
            order.id.toLowerCase().includes(term) ||
            order.customer.toLowerCase().includes(term)
          );
        }

        // Date range
        if (customerStartDate || customerEndDate) {
          const orderDate = new Date(order.date);
          if (customerStartDate && orderDate < new Date(customerStartDate)) return false;
          if (customerEndDate && orderDate > new Date(customerEndDate)) return false;
        }

        return true;
      })
      .sort((a, b) => {
        switch (customerSortBy) {
          case "date-asc":
            return new Date(a.date) - new Date(b.date);
          case "date-desc":
            return new Date(b.date) - new Date(a.date);
          case "amount-asc":
            return a.amount - b.amount;
          case "amount-desc":
            return b.amount - b.amount;
          default:
            return 0;
        }
      });

    const totalCustomerPages = Math.ceil(filteredCustomerOrders.length / customerItemsPerPage);
    const paginatedCustomerOrders = filteredCustomerOrders.slice(
      (customerCurrentPage - 1) * customerItemsPerPage,
      customerCurrentPage * customerItemsPerPage
    );
    // Validation Errors
    const [errors, setErrors] = useState({});

    const validateForm = () => {
      const newErrors = {};

      if (!basketType) newErrors.basketType = "Please select a basket type";
      if (!category) newErrors.category = "Please select a category";
      if (basketType === "Fixed Amount" && !amount) {
        newErrors.amount = "Please select amount for Fixed Amount basket";
      }
      if (!supplierCode.trim()) newErrors.supplierCode = "Supplier Code is required";

      // At least one item must be selected
      if (Object.keys(selectedItems).length === 0) {
        newErrors.items = "Please select at least one item";
      }

      // Validate each selected item
      Object.keys(selectedItems).forEach(item => {
        const itemData = selectedItems[item];
        
        if (!itemData.mode) {
          newErrors[`item_${item}_mode`] = "Please select mode (Amount or Qty)";
        }

        if (itemData.mode === "amount") {
          if (!itemData.amount || parseFloat(itemData.amount) <= 0) {
            newErrors[`item_${item}_amount`] = "Amount must be greater than 0";
          }
        }

        if (itemData.mode === "qty") {
          if (!itemData.qty || parseFloat(itemData.qty) <= 0) {
            newErrors[`item_${item}_qty`] = "Quantity must be greater than 0";
          }
          if (!itemData.unit) {
            newErrors[`item_${item}_unit`] = "Please select a unit";
          }
        }
      });

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleItemChange = (item, field, value) => {
      setSelectedItems((prev) => ({
        ...prev,
        [item]: {
          ...prev[item],
          [field]: value,
        },
      }));
      // Clear error for this field when user corrects it
      if (errors[`item_${item}_${field}`]) {
        setErrors(prev => {
          const updated = { ...prev };
          delete updated[`item_${item}_${field}`];
          return updated;
        });
      }
    };

    const toggleItem = (item) => {
      setSelectedItems((prev) => {
        const updated = { ...prev };
        if (updated[item]) {
          delete updated[item];
        } else {
          updated[item] = { mode: "amount" };
        }
        return updated;
      });
      // Clear item-related errors when toggling
      setErrors(prev => {
        const updated = { ...prev };
        delete updated.items;
        delete updated[`item_${item}_mode`];
        delete updated[`item_${item}_amount`];
        delete updated[`item_${item}_qty`];
        delete updated[`item_${item}_unit`];
        return updated;
      });
    };

    const loadDemo = () => {
      setBasketType(demoOrder.basketType);
      setCategory(demoOrder.category);
      setAmount(demoOrder.amount);
      setProcessing(demoOrder.processing);
      setSupplierCode(demoOrder.supplierCode);
      setBoutiqueCode(demoOrder.boutiqueCode);
      setReffererCode(demoOrder.reffererCode);
      setResellerCode(demoOrder.resellerCode);
      setCouponCode(demoOrder.couponCode);
      setGroupCode(demoOrder.groupCode);
      setSelectedItems(demoOrder.items);
      setFinalRemarks(demoOrder.finalRemarks);
      setVoiceInput(demoOrder.setVoiceInput)
      setErrors({}); // Clear errors
    };

    const resetForm = () => {
      if (!window.confirm("Reset form?")) return;
      setBasketType("");
      setCategory("");
      setAmount("");
      setProcessing("");
      setSupplierCode("");
      setBoutiqueCode("");
      setReffererCode("");
      setResellerCode("");
      setCouponCode("");
      setGroupCode("");      
      setSelectedItems({});
      setFinalRemarks("");
      setVoiceInput("");
      setErrors({});
      setView("form");
    };

    const loadRepeat = () => {
      if (repeatOrderId === "ORD001") {
        loadDemo();
        alert("Previous order loaded ✅");
        setView("form");
      } else {
        alert("Order not found ❌");
      }
    };

    const handleVerify = (type) => {
      const code = {
        supplier: supplierCode,
        boutique: boutiqueCode,
        referrer: reffererCode,
        reseller: resellerCode,
        coupon: couponCode,
        group: groupCode,
      }[type];

      if (!code || code.trim() === "") {
        alert(`⚠️ Please enter ${type} code first.`);
        return;
      }

      alert(`✅ Verifying ${type} code: ${code}...\n\n( Dummy verification - In real app this will call API )`);
      // Future: Call your verification API here
    };

    const handleApply = (type) => {
      const code = {
        supplier: supplierCode,
        boutique: boutiqueCode,
        referrer: reffererCode,
        reseller: resellerCode,
        coupon: couponCode,
        group: groupCode,
      }[type];

      if (!code || code.trim() === "") {
        alert(`⚠️ Please enter ${type} code first.`);
        return;
      }

      alert(`✅ ${type.charAt(0).toUpperCase() + type.slice(1)} code applied successfully!`);
      // Future: Apply logic (discount, validation, etc.)
    };

    const handleSaveDraft = () => {
      if (!supplierCode) {
        alert("⚠️ Supplier Code is required to save draft.");
        return;
      }

      const draftData = {
        basketType,
        category,
        amount,
        processing,
        supplierCode,
        boutiqueCode,
        reffererCode,
        resellerCode,
        couponCode,
        groupCode,
        items: selectedItems,
        finalRemarks,
        savedAt: new Date().toISOString(),
      };

      // console.log("Draft Saved:", draftData);
      
      // You can later store this in localStorage, IndexedDB, or send to backend
      localStorage.setItem(`draft_${Date.now()}`, JSON.stringify(draftData));

      alert("✅ Order saved as draft successfully!\nYou can load it later from My Orders.");
    };

    const handleNotifyGroup = () => {
      if (!supplierCode) {
        alert("⚠️ Supplier Code is required.");
        return;
      }
      if (Object.keys(selectedItems).length === 0) {
        alert("⚠️ Please select at least one item before notifying group members.");
        return;
      }

      alert(`👥 Notification sent to Group Members!\n\nOrder details for Supplier: ${supplierCode} has been shared.`);
      // Future: Real notification logic / API call here
    };

    const handleSubmit = () => {
      if (!validateForm()) {
        alert("Please fix the errors in the form ❌");
        return;
      }

      const payload = {
        basketType,
        category,
        amount,
        processing,
        supplierCode,
        boutiqueCode,
        reffererCode,
        resellerCode,
        couponCode,
        groupCode,
        items: selectedItems,
        finalRemarks,
      };

      // console.log("Order Submitted:", payload);
      alert(`Order placed successfully to ${supplierCode} ✅`);
      
      // Optional: Reset form after successful submission
      // resetForm();
    };

    const [showPaymentOptions, setShowPaymentOptions] = useState(false);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");

    const handlePlaceOrderClick = () => {
      if (!supplierCode) {
        alert("⚠️ Please enter Supplier Code before placing order.");
        return;
      }
      setShowPaymentOptions(true);
      setSelectedPaymentMethod(""); // Reset selection
    };

    const handlePaymentSelect = (method) => {
      setSelectedPaymentMethod(method);
    };

    const handleConfirmOrder = () => {
      if (!selectedPaymentMethod) return;

      const finalPayload = {
        ...{
          basketType,
          category,
          amount,
          processing,
          supplierCode,
          boutiqueCode,
          reffererCode,
          resellerCode,
          couponCode,
          groupCode,
          items: selectedItems,
          finalRemarks,
        },
        paymentMethod: selectedPaymentMethod,
        deliveryType: selectedPaymentMethod.includes("Home") ? "Home Delivery" : "Shop Pickup",
      };

      // console.log("Final Order Confirmed:", finalPayload);
      alert(`🎉 Order placed successfully!\nPayment Method: ${selectedPaymentMethod}\nStatus: Processing`);

      // Optional: Reset form after successful order
      // resetForm();
      setShowPaymentOptions(false);
      setSelectedPaymentMethod("");
    };

    const getError = (key) => errors[key] ? (
      <p className="text-red-500 text-xs mt-1">{errors[key]}</p>
    ) : null;

    // Special Offer Component (Separate to follow Rules of Hooks)
    const OfferSection = ({ item, supplierCode }) => {
    const [showOffer, setShowOffer] = useState(false);

    return (
      <div className="mt-3 bg-gradient-to-r from-amber-900/80 to-yellow-900/60 border border-amber-600 rounded p-3">
        <div className="flex items-center justify-between">
          <div className="text-amber-300 text-sm font-medium">
            This <span className="font-mono text-amber-200">{supplierCode}</span> has Special Offer for You
          </div>
          <button
            onClick={() => setShowOffer(!showOffer)}
            className="text-xs w-48 p-2 bg-amber-700 hover:bg-amber-600 rounded font-medium transition"
          >
            {showOffer ? "Hide Offer" : "Click To View Offer"}
          </button>
        </div>

        {showOffer && (
          <div className="mt-3 text-sm text-amber-200 bg-black/40 p-3 rounded border border-amber-700/50">
            🎉 Special Offer: Buy 5kg or more of <strong>{item}</strong> and get 500g <strong>FREE</strong>!<br />
            Valid till this week only. Limited stock.
          </div>
        )}
      </div>
    );
  };

  // Reusable Help Icon Component
  const HelpIcon = ({ helpText }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
      <div className="relative">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          className="bg-cyan-600 text-gray-100 hover:text-teal-400 text-xl border border-cyan-200"
          title="Help"
        >
          ?
        </button>

        {isOpen && (
          <div className="absolute left-20 top-8 z-50 w-72 bg-gray-800 border border-gray-600 rounded-lg p-4 shadow-xl text-sm text-gray-200">
            <div className="font-medium text-teal-300 mb-2">Help</div>
            <p className="leading-relaxed text-white">{helpText}</p>
            
            <button
              onClick={() => setIsOpen(false)}
              className="mt-3 py-2 text-xs text-gray-100 hover:text-white underline"
            >
              Close
            </button>
          </div>
        )}
      </div>
    );
  };

    return (
      <div className="min-h-screen flex flex-col bg-black text-gray-100 font-sans">
        <header className="sticky top-0 z-50 bg-black border-b border-blue-400 px-4 py-3 flex items-center justify-between">
          <div className="flex flex-col">
            <div className="text-lg lg:text-xl font-bold text-yellow-200">
              Budget Basket 
            </div>
            <div className="text-[11px] text-gray-200">
              AI Powered Budget Basket / Shopping Cart For Households!
            </div>
          </div>

          <div className="hidden md:flex items-center gap-3">
            {isLoggedIn ? (
              <>
                <div className="text-xs lg:text-sm text-teal-300">{userName}</div>
                <button
                  onClick={handleLogout}
                  className="w-8 mt-2 px-2 py-1 rounded-md bg-red-600 hover:bg-red-700 transition"
                  title="Logout"
                >
                  ⏻
                </button>
              </>
            ) : (
              <button
                onClick={() => navigate("/login")}
                className="w-8 px-2 py-1 rounded-full border border-gray-700 text-sm text-gray-200 hover:bg-gray-800"
              >
                🔐
              </button>
            )}
          </div>
          <button
            onClick={() => setIsMobileMenuOpen((s) => !s)}
            className="md:hidden w-8 h-8 flex items-center justify-center text-2xl text-teal-300"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? "×" : "☰"}
          </button>
        </header>

        {isMobileMenuOpen && (
          <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-3">
            {isLoggedIn ? (
              <div className="flex flex-col items-center gap-3">
                <div className="text-teal-300 text-xs lg:text-sm font-medium">
                  {userName}
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-md bg-red-600 hover:bg-red-700"
                >
                  ⏻
                </button>
              </div>
            ) : (
              <div className="flex justify-center gap-6">
                <Link to="/login" className="text-teal-300">🔐</Link>
              </div>
            )}
          </div>
        )}

        {/* Top Action Buttons */}
        <div className="grid grid-cols-3 lg:grid-cols-7 gap-2 mb-4">
          <button className="bg-gray-800 p-2 rounded" onClick={loadDemo}>Demo Form</button>
          <button className="bg-gray-800 p-2 rounded" onClick={loadDemo}>Load Draft</button>
          <button className="bg-gray-800 p-2 rounded" onClick={resetForm}>Prepare New Basket</button>
          <button className="bg-gray-800 p-2 rounded" onClick={() => setView("myOrders")}>My Orders</button>
          <button className="bg-gray-800 p-2 rounded" onClick={() => setView("repeat")}>Repeat Previous</button>
          <button className="bg-gray-800 p-2 rounded" onClick={() => setView("customerOrders")}>Customer Orders</button>
          <button className="bg-red-700 p-2 rounded" onClick={resetForm}>Reset Form</button>
        </div>

        {/* Views */}
        {view === "myOrders" && (
          <div>
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
              <h2 className="text-xl font-semibold">My Orders</h2>
              
              {/* Filters, Search & Sorting */}
              <div className="flex flex-col sm:flex-row gap-3 flex-1 max-w-3xl">
                {/* Search */}
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder="Search by Order ID, Supplier or Amount..."
                    className="w-full bg-gray-900 border border-gray-700 rounded px-4 py-2 pl-10 text-sm"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1); // Reset to first page on search
                    }}
                  />
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500">🔍</span>
                </div>

                {/* Status Filter */}
                <select
                  value={myOrderFilter}
                  onChange={(e) => {
                    setMyOrderFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="bg-gray-900 border border-gray-700 rounded px-4 py-2 text-sm min-w-[140px]"
                >
                  <option value="all">All Status</option>
                  <option value="Delivered">Delivered Only</option>
                  <option value="Pending">Pending Only</option>
                </select>

                {/* Sorting */}
                <select
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="bg-gray-900 border border-gray-700 rounded px-4 py-2 text-sm min-w-[160px]"
                >
                  <option value="date-desc">Newest First</option>
                  <option value="date-asc">Oldest First</option>
                  <option value="amount-desc">Highest Amount</option>
                  <option value="amount-asc">Lowest Amount</option>
                </select>

                {/* Date Range */}
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm"
                  />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm"
                  />
                </div>

                <button 
                  onClick={() => setView("form")}
                  className="text-sm bg-gray-800 hover:bg-gray-700 px-5 py-2 rounded whitespace-nowrap"
                >
                  ← Back to Form
                </button>
              </div>
            </div>

            {/* Active Filters Summary */}
            {(searchTerm || startDate || endDate || myOrderFilter !== "all" || sortBy !== "date-desc") && (
              <div className="mb-4 flex flex-wrap gap-2 text-xs">
                {searchTerm && (
                  <div className="bg-gray-800 px-3 py-1 rounded-full flex items-center gap-2">
                    Search: "{searchTerm}"
                    <button onClick={() => setSearchTerm("")} className="text-red-400 hover:text-red-300 ml-1">×</button>
                  </div>
                )}
                {myOrderFilter !== "all" && (
                  <div className="bg-gray-800 px-3 py-1 rounded-full">Status: {myOrderFilter}</div>
                )}
                {startDate && <div className="bg-gray-800 px-3 py-1 rounded-full">From: {startDate}</div>}
                {endDate && <div className="bg-gray-800 px-3 py-1 rounded-full">To: {endDate}</div>}
                {sortBy !== "date-desc" && (
                  <div className="bg-gray-800 px-3 py-1 rounded-full">
                    Sorted: {sortBy === "date-asc" ? "Oldest" : sortBy === "amount-desc" ? "Highest Amount" : "Lowest Amount"}
                  </div>
                )}
              </div>
            )}

            <div className="space-y-4">
              {paginatedMyOrders.length > 0 ? (
                paginatedMyOrders.map((o) => {
                  const isExpanded = expandedOrder === o.id;
                  const isDemoOrder = o.id === "ORD001";
                  const orderDetails = isDemoOrder ? demoOrder : null;

                  return (
                    <div 
                      key={o.id} 
                      className="border border-gray-700 bg-gray-900 rounded-lg overflow-hidden cursor-pointer hover:border-teal-600 transition-all"
                      onClick={() => setExpandedOrder(isExpanded ? null : o.id)}
                    >
                      {/* Order Summary Header */}
                      <div className="p-5 flex flex-wrap items-center justify-between gap-4 hover:bg-gray-800/50 transition">
                        <div className="flex items-center gap-4">
                          <div>
                            <div className="font-mono text-xl font-bold text-teal-300">{o.id}</div>
                            <div className="text-sm text-gray-400 mt-0.5">{o.date}</div>
                          </div>
                          <div className={`px-4 py-1 text-xs font-medium rounded-full ${
                            o.status === "Delivered" 
                              ? "bg-green-900/80 text-green-300" 
                              : "bg-yellow-900/80 text-yellow-300"
                          }`}>
                            {o.status}
                          </div>
                        </div>

                        <div className="flex items-center gap-8">
                          <div className="text-right">
                            <div className="text-2xl font-semibold text-white">₹{o.amount}</div>
                            <div className="text-xs text-gray-400">to {o.supplier}</div>
                          </div>
                          
                          <div className={`text-3xl text-gray-400 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                            ▼
                          </div>
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="border-t border-gray-700 bg-black/60 p-6 space-y-7">
                          {isDemoOrder && orderDetails ? (
                            <>
                              <div>
                                <h3 className="uppercase text-xs tracking-widest text-gray-500 mb-3">Order Information</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm">
                                  <div><span className="text-gray-400">Basket Type:</span> <span className="font-medium">{orderDetails.basketType}</span></div>
                                  <div><span className="text-gray-400">Category:</span> <span className="font-medium">{orderDetails.category}</span></div>
                                  {orderDetails.amount && (
                                    <div><span className="text-gray-400">Fixed Amount:</span> <span className="font-medium">₹{orderDetails.amount}</span></div>
                                  )}
                                  <div><span className="text-gray-400">Processing:</span> <span className="font-medium">{orderDetails.processing}</span></div>
                                </div>
                              </div>

                              <div>
                                <h3 className="uppercase text-xs tracking-widest text-gray-500 mb-4">Items</h3>
                                <div className="space-y-4">
                                  {Object.entries(orderDetails.items).map(([itemName, details]) => (
                                    <div key={itemName} className="bg-gray-950 border border-gray-700 rounded-xl p-5">
                                      <div className="font-bold text-lg mb-4">{itemName}</div>
                                      <div className="grid grid-cols-1 md:grid-cols-3 gap-y-4 text-sm">
                                        <div>
                                          <div className="text-gray-400 text-xs">MODE</div>
                                          <div className="capitalize font-medium mt-1">{details.mode}</div>
                                        </div>
                                        {details.mode === "amount" && (
                                          <div>
                                            <div className="text-gray-400 text-xs">AMOUNT</div>
                                            <div className="font-medium mt-1">₹{details.amount}</div>
                                          </div>
                                        )}
                                        {details.mode === "qty" && (
                                          <div>
                                            <div className="text-gray-400 text-xs">QUANTITY</div>
                                            <div className="font-medium mt-1">{details.qty} {details.unit}</div>
                                          </div>
                                        )}
                                        {details.instruction && (
                                          <div>
                                            <div className="text-gray-400 text-xs">INSTRUCTION</div>
                                            <div className="italic text-gray-300 mt-1">"{details.instruction}"</div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {orderDetails.finalRemarks && (
                                <div>
                                  <h3 className="uppercase text-xs tracking-widest text-gray-500 mb-2">Final Remarks</h3>
                                  <div className="bg-gray-950 border border-gray-700 rounded-xl p-5 italic text-gray-200">
                                    {orderDetails.finalRemarks}
                                  </div>
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="py-12 text-center text-gray-400 italic">
                              Detailed order information will be fetched from database.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="bg-gray-900 border border-gray-700 rounded-xl py-16 text-center">
                  <div className="text-4xl mb-4">📭</div>
                  <p className="text-gray-400">No orders found matching your filters.</p>
                  <button 
                    onClick={() => {
                      setSearchTerm("");
                      setStartDate("");
                      setEndDate("");
                      setMyOrderFilter("all");
                      setSortBy("date-desc");
                      setCurrentPage(1);
                    }}
                    className="mt-6 text-teal-400 hover:text-teal-300 underline text-sm"
                  >
                    Clear all filters
                  </button>
                </div>
              )}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-8">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 rounded text-sm"
                >
                  Previous
                </button>
                
                <div className="flex gap-2">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-9 h-9 rounded flex items-center justify-center text-sm transition ${
                        currentPage === page 
                          ? "bg-teal-600 text-white" 
                          : "bg-gray-800 hover:bg-gray-700"
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 rounded text-sm"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        {view === "customerOrders" && (
          <div>
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
              <h2 className="text-xl font-semibold">Customer Orders</h2>
              
              {/* Filters, Search & Sorting */}
              <div className="flex flex-col sm:flex-row gap-3 flex-1 max-w-3xl">
                {/* Search */}
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder="Search by Order ID or Customer ID..."
                    className="w-full bg-gray-900 border border-gray-700 rounded px-4 py-2 pl-10 text-sm"
                    value={customerSearchTerm}
                    onChange={(e) => {
                      setCustomerSearchTerm(e.target.value);
                      setCustomerCurrentPage(1);
                    }}
                  />
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500">🔍</span>
                </div>

                {/* Status Filter */}
                <select
                  value={customerOrderFilter}
                  onChange={(e) => {
                    setCustomerOrderFilter(e.target.value);
                    setCustomerCurrentPage(1);
                  }}
                  className="bg-gray-900 border border-gray-700 rounded px-4 py-2 text-sm min-w-[140px]"
                >
                  <option value="all">All Status</option>
                  <option value="Delivered">Delivered Only</option>
                  <option value="Pending">Pending Only</option>
                </select>

                {/* Sorting */}
                <select
                  value={customerSortBy}
                  onChange={(e) => {
                    setCustomerSortBy(e.target.value);
                    setCustomerCurrentPage(1);
                  }}
                  className="bg-gray-900 border border-gray-700 rounded px-4 py-2 text-sm min-w-[160px]"
                >
                  <option value="date-desc">Newest First</option>
                  <option value="date-asc">Oldest First</option>
                  <option value="amount-desc">Highest Amount</option>
                  <option value="amount-asc">Lowest Amount</option>
                </select>

                {/* Date Range */}
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={customerStartDate}
                    onChange={(e) => {
                      setCustomerStartDate(e.target.value);
                      setCustomerCurrentPage(1);
                    }}
                    className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm"
                  />
                  <input
                    type="date"
                    value={customerEndDate}
                    onChange={(e) => {
                      setCustomerEndDate(e.target.value);
                      setCustomerCurrentPage(1);
                    }}
                    className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm"
                  />
                </div>

                <button 
                  onClick={() => setView("form")}
                  className="text-sm bg-gray-800 hover:bg-gray-700 px-5 py-2 rounded whitespace-nowrap"
                >
                  ← Back to Form
                </button>
              </div>
            </div>

            {/* Active Filters Summary */}
            {(customerSearchTerm || customerStartDate || customerEndDate || customerOrderFilter !== "all" || customerSortBy !== "date-desc") && (
              <div className="mb-4 flex flex-wrap gap-2 text-xs">
                {customerSearchTerm && (
                  <div className="bg-gray-800 px-3 py-1 rounded-full flex items-center gap-2">
                    Search: "{customerSearchTerm}"
                    <button onClick={() => setCustomerSearchTerm("")} className="text-red-400 hover:text-red-300 ml-1">×</button>
                  </div>
                )}
                {customerOrderFilter !== "all" && (
                  <div className="bg-gray-800 px-3 py-1 rounded-full">Status: {customerOrderFilter}</div>
                )}
                {customerStartDate && <div className="bg-gray-800 px-3 py-1 rounded-full">From: {customerStartDate}</div>}
                {customerEndDate && <div className="bg-gray-800 px-3 py-1 rounded-full">To: {customerEndDate}</div>}
                {customerSortBy !== "date-desc" && (
                  <div className="bg-gray-800 px-3 py-1 rounded-full">
                    Sorted: {customerSortBy === "date-asc" ? "Oldest" : customerSortBy === "amount-desc" ? "Highest Amount" : "Lowest Amount"}
                  </div>
                )}
              </div>
            )}

            <div className="space-y-4">
              {paginatedCustomerOrders.length > 0 ? (
                paginatedCustomerOrders.map((o) => {
                  const isExpanded = expandedCustomerOrder === o.id;

                  return (
                    <div 
                      key={o.id} 
                      className="border border-gray-700 bg-gray-900 rounded-lg overflow-hidden cursor-pointer hover:border-teal-600 transition-all"
                      onClick={() => setExpandedCustomerOrder(isExpanded ? null : o.id)}
                    >
                      {/* Order Summary Header */}
                      <div className="p-5 flex flex-wrap items-center justify-between gap-4 hover:bg-gray-800/50 transition">
                        <div className="flex items-center gap-4">
                          <div>
                            <div className="font-mono text-xl font-bold text-teal-300">{o.id}</div>
                            <div className="text-sm text-gray-400 mt-0.5">{o.date}</div>
                          </div>
                          <div className={`px-4 py-1 text-xs font-medium rounded-full ${
                            o.status === "Delivered" 
                              ? "bg-green-900/80 text-green-300" 
                              : "bg-yellow-900/80 text-yellow-300"
                          }`}>
                            {o.status}
                          </div>
                        </div>

                        <div className="flex items-center gap-8">
                          <div className="text-right">
                            <div className="text-2xl font-semibold text-white">₹{o.amount}</div>
                            <div className="text-xs text-gray-400">Customer: {o.customer}</div>
                          </div>
                          
                          <div className={`text-3xl text-gray-400 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                            ▼
                          </div>
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="border-t border-gray-700 bg-black/60 p-8 text-center text-gray-400 italic">
                          Full detailed customer order breakdown (items, remarks, etc.) will be loaded from the database in the next update.
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="bg-gray-900 border border-gray-700 rounded-xl py-16 text-center">
                  <div className="text-4xl mb-4">📭</div>
                  <p className="text-gray-400">No customer orders found matching your filters.</p>
                  <button 
                    onClick={() => {
                      setCustomerSearchTerm("");
                      setCustomerStartDate("");
                      setCustomerEndDate("");
                      setCustomerOrderFilter("all");
                      setCustomerSortBy("date-desc");
                      setCustomerCurrentPage(1);
                    }}
                    className="mt-6 text-teal-400 hover:text-teal-300 underline text-sm"
                  >
                    Clear all filters
                  </button>
                </div>
              )}
            </div>

            {/* Pagination Controls */}
            {totalCustomerPages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-8">
                <button
                  onClick={() => setCustomerCurrentPage(p => Math.max(1, p - 1))}
                  disabled={customerCurrentPage === 1}
                  className="px-4 py-2 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 rounded text-sm"
                >
                  Previous
                </button>
                
                <div className="flex gap-2">
                  {Array.from({ length: totalCustomerPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCustomerCurrentPage(page)}
                      className={`w-9 h-9 rounded flex items-center justify-center text-sm transition ${
                        customerCurrentPage === page 
                          ? "bg-teal-600 text-white" 
                          : "bg-gray-800 hover:bg-gray-700"
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => setCustomerCurrentPage(p => Math.min(totalCustomerPages, p + 1))}
                  disabled={customerCurrentPage === totalCustomerPages}
                  className="px-4 py-2 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 rounded text-sm"
                >
                  Next
                </button>
              </div>
            )}
        </div>
      )}

        {view === "repeat" && (
          <div className="mb-4">
            <input
              placeholder="Enter Order#"
              className="p-2 bg-gray-900 border border-gray-700 rounded mr-2"
              value={repeatOrderId}
              onChange={(e) => setRepeatOrderId(e.target.value)}
            />
            <button className="bg-white text-black px-3 py-2 rounded" onClick={loadRepeat}>Get Data</button>
          </div>
        )}


        {view === "form" && (
          <div className="p-4 grid gap-4 border border-cyan-200">
            {/* Basket Type */}
            <div className="flex flex-col">
              <label className="mb-1">Select Basket Type</label>
              <select
                className="h-12 w-64 p-2 bg-gray-900 border border-gray-700 rounded"
                value={basketType}
                onChange={(e) => {
                  setBasketType(e.target.value);
                  setAmount("");
                  if (errors.basketType) {
                    setErrors(prev => { const updated = {...prev}; delete updated.basketType; return updated; });
                  }
                }}
              >
                <option value="">Choose...</option>
                {basketTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              {getError("basketType")}
            </div>

            {/* Category */}
            <div className="flex flex-col">
              <label className="mb-1">Select Category</label>
              <select
                className="h-12 w-64 p-2 bg-gray-900 border border-gray-700 rounded"
                value={category}
                onChange={(e) => {
                  setCategory(e.target.value);
                  if (errors.category) {
                    setErrors(prev => { const updated = {...prev}; delete updated.category; return updated; });
                  }
                }}
              >
                <option value="">Choose...</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              {getError("category")}
            </div>

            {/* Amount Dropdown (only for Fixed Amount) */}
            {basketType === "Fixed Amount" && (
              <div className="flex flex-col">
                <label className="mb-1">Select Amount (₹)</label>
                <select
                  className="h-12 w-64 p-2 bg-gray-900 border border-gray-700 rounded"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    if (errors.amount) {
                      setErrors(prev => { const updated = {...prev}; delete updated.amount; return updated; });
                    }
                  }}
                >
                  <option value="">Choose Amount</option>
                  {amountOptions.map((amt) => (
                    <option key={amt} value={amt}>₹{amt}</option>
                  ))}
                </select>
                {getError("amount")}
              </div>
            )}

            {/* Processing Type */}
            <div className="flex flex-col">
              <label className="mb-1">Select Finish Type</label>
              <select
                className="h-12 w-64 p-2 bg-gray-900 border border-gray-700 rounded"
                value={processing}
                onChange={(e) => setProcessing(e.target.value)}
              >
                <option value="">Choose...</option>
                {processingTypes.map((proc) => (
                  <option key={proc} value={proc}>{proc}</option>
                ))}
              </select>
            </div>

            {/* Supplier Code */}
            <div className="flex flex-col">
              <div className="flex flex-row mb-1">
                <label className="text-sm">Supplier Code</label>
                <HelpIcon 
                  helpText="Unique code assigned to the supplier. This is mandatory to place an order." 
                />
              </div>
              <div className="flex gap-2">
                <input
                  className="flex-1 p-3 bg-gray-900 border border-gray-700 rounded"
                  value={supplierCode}
                  onChange={(e) => {
                    setSupplierCode(e.target.value);
                    if (errors.supplierCode) {
                      setErrors(prev => { const updated = {...prev}; delete updated.supplierCode; return updated; });
                    }
                  }}
                  placeholder="Enter Supplier Code"
                />
                <button
                  onClick={() => handleVerify("supplier")}
                  className="px-6 w-40 bg-teal-600 hover:bg-teal-700 text-white rounded font-medium transition"
                >
                  Verify
                </button>
                <button
                  onClick={() => handleApply("supplier")}
                  className="px-6 w-40 bg-orange-500 hover:bg-orange-600 text-black rounded font-medium transition"
                >
                  Apply
                </button>
              </div>
              {getError("supplierCode")}
            </div>

            {/* Boutique Code */}
            <div className="flex flex-col">
              <div className="flex flex-row mb-1">
                <label className="text-sm">Boutique Code</label>
                <HelpIcon 
                  helpText="Code for the specific boutique or outlet under the supplier." 
                />
              </div>
              <div className="flex gap-2">
                <input
                  className="flex-1 p-3 bg-gray-900 border border-gray-700 rounded"
                  value={boutiqueCode}
                  onChange={(e) => setBoutiqueCode(e.target.value)}
                  placeholder="Enter Boutique Code"
                />
                <button
                  onClick={() => handleVerify("boutique")}
                  className="px-6 w-40 bg-teal-600 hover:bg-teal-700 text-white rounded font-medium transition"
                >
                  Verify
                </button>
                <button
                  onClick={() => handleApply("boutique")}
                  className="px-6 w-40 bg-orange-500 hover:bg-orange-600 text-black rounded font-medium transition"
                >
                  Apply
                </button>
              </div>
              {getError("boutiqueCode")}
            </div>

            {/* ValueShare Referrer Code */}
            <div className="flex flex-col">
              <div className="flex flex-row mb-1">
                <label className="text-sm">ValueShare Referrer Code</label>
                <HelpIcon 
                  helpText="Referral code from ValueShare partner. Used for commission tracking." 
                />
              </div>
              <div className="flex gap-2">
                <input
                  className="flex-1 p-3 bg-gray-900 border border-gray-700 rounded"
                  value={reffererCode}
                  onChange={(e) => setReffererCode(e.target.value)}
                  placeholder="Enter ValueShare Referrer Code"
                />
                <button
                  onClick={() => handleVerify("referrer")}
                  className="px-6 w-40 bg-teal-600 hover:bg-teal-700 text-white rounded font-medium transition"
                >
                  Verify
                </button>
                <button
                  onClick={() => handleApply("referrer")}
                  className="px-6 w-40 bg-orange-500 hover:bg-orange-600 text-black rounded font-medium transition"
                >
                  Apply
                </button>
              </div>
              {getError("reffererCode")}
            </div>

            {/* Reseller Code */}
            <div className="flex flex-col">
              <div className="flex flex-row mb-1">
                <label className="text-sm">Reseller Code</label>
                <HelpIcon 
                  helpText="Code for resellers or distributors who are selling on behalf of the supplier." 
                />
              </div>
              <div className="flex gap-2">
                <input
                  className="flex-1 p-3 bg-gray-900 border border-gray-700 rounded"
                  value={resellerCode}
                  onChange={(e) => setResellerCode(e.target.value)}
                  placeholder="Enter Reseller Code"
                />
                <button
                  onClick={() => handleVerify("reseller")}
                  className="px-6 w-40 bg-teal-600 hover:bg-teal-700 text-white rounded font-medium transition"
                >
                  Verify
                </button>
                <button
                  onClick={() => handleApply("reseller")}
                  className="px-6 w-40 bg-orange-500 hover:bg-orange-600 text-black rounded font-medium transition"
                >
                  Apply
                </button>
              </div>
              {getError("resellerCode")}
            </div>

            {/* Coupon Code */}
            <div className="flex flex-col">
              <div className="flex flex-row mb-1">
                <label className="text-sm">Coupon Code</label>
                <HelpIcon 
                  helpText="Enter any active promotional or discount coupon code." 
                />
              </div>
              <div className="flex gap-2">
                <input
                  className="flex-1 p-3 bg-gray-900 border border-gray-700 rounded"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  placeholder="Enter Coupon Code"
                />
                <button
                  onClick={() => handleVerify("coupon")}
                  className="px-6 w-40 bg-teal-600 hover:bg-teal-700 text-white rounded font-medium transition"
                >
                  Verify
                </button>
                <button
                  onClick={() => handleApply("coupon")}
                  className="px-6 w-40 bg-orange-500 hover:bg-orange-600 text-black rounded font-medium transition"
                >
                  Apply
                </button>
              </div>
              {getError("couponCode")}
            </div>

            {/* Customer Group Code */}
            <div className="flex flex-col">
              <div className="flex flex-row mb-1">
                <label className="text-sm">Group Code</label>
                <HelpIcon 
                  helpText="Customer group or membership code for special group discount pricing and benefits." 
                />
              </div>
              <div className="flex gap-2">
                <input
                  className="flex-1 p-3 bg-gray-900 border border-gray-700 rounded"
                  value={groupCode}
                  onChange={(e) => setGroupCode(e.target.value)}
                  placeholder="Enter Group Code"
                />
                <button
                  onClick={() => handleVerify("group")}
                  className="px-6 w-40 bg-teal-600 hover:bg-teal-700 text-white rounded font-medium transition"
                >
                  Verify
                </button>
                <button
                  onClick={() => handleApply("group")}
                  className="px-6 w-40 bg-orange-500 hover:bg-orange-600 text-black rounded font-medium transition"
                >
                  Apply
                </button>
              </div>
              {getError("groupCode")}
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 mt-3">
              <button 
                className="flex-1 w-32 bg-teal-600 hover:bg-teal-700 text-white py-2 px-4 rounded text-sm font-medium transition"
                onClick={() => alert("Opening Shop for " + (supplierCode || "selected supplier"))}
              >
                View Shop
              </button>
              <button 
                className="flex-1 w-40 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-sm font-medium transition"
                onClick={() => alert("Connecting you to seller...")}
              >
                Talk To Seller
              </button>
              <button 
                className="flex-1 w-40 bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded text-sm font-medium transition"
                onClick={() => alert("Opening chat with seller...")}
              >
                Chat With Seller
              </button>
            </div>

            {/* Language Selector */}
            <div className="mt-4">
              <label className="mb-1 block text-sm">Select Language (Item names will appear in chosen language)</label>
              <select
                className="h-11 w-full p-2 bg-gray-900 border border-gray-700 rounded text-sm"
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
              >
                <option value="en">English</option>
                <option value="hi">हिंदी (Hindi)</option>
                <option value="mr">मराठी (Marathi)</option>
                <option value="ta">தமிழ் (Tamil)</option>
                <option value="te">తెలుగు (Telugu)</option>
                <option value="kn">ಕನ್ನಡ (Kannada)</option>
              </select>
            </div>

            <div className="text-lg lg:text-xl mt-2 text-yellow-200">📹 This Shop Has Thinky IZC Digital Twin Camera. You Can View The Item Live While Shopping</div>
            
            {/* AI Assistant Buttons - Grid Layout */}
            <div className="mt-2 grid grid-cols-2 lg:grid-cols-3 place-items-center gap-1">            
                <button
                  onClick={() => setShowAIResponse(!showAIResponse)}
                  className="flex flex-col items-center justify-center w-full gap-2 bg-gradient-to-br from-purple-900 to-indigo-900 hover:from-purple-800 hover:to-indigo-800 border border-purple-500/50 p-2 rounded-xl"
                >
                  <span className="text-2xl">🛒</span>
                  <span className="text-sm font-medium text-center">
                    Auto Generate<br />Complete Order With AI
                  </span>
                </button>

                <button
                  // onClick={() => alert("🎤 Voice Input mode activated.\n\nYou can now speak your order requirements.")}
                  onClick={() => setEnableVoiceInput(!enableVoiceInput)}
                  className="flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-cyan-900 to-teal-900 hover:from-cyan-800 hover:to-teal-800 border border-cyan-500/50 p-2 rounded-xl"
                >
                  <span className="text-2xl">🎤</span>
                  <span className="text-sm font-medium text-center">
                    Enable Voice<br />Input
                  </span>
                </button>

                <button
                  onClick={() => alert("📢 Voice recording started for seller.\n\nSpeak your instructions clearly...")}
                  className="flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-amber-900 to-orange-900 hover:from-amber-800 hover:to-orange-800 border border-amber-500/50 p-2 rounded-xl"
                >
                  <span className="text-2xl">📢</span>
                  <span className="text-sm font-medium text-center">
                    Record Voice<br />For Seller
                  </span>
                </button>
            </div>

            {/* AILightComponent - Only shown when Auto Generate is clicked */}
            {showAIResponse && (
              <div className="mt-6 p-4 border border-purple-700 bg-gray-950 rounded-xl">
                <div className="flex flex-col mb-3">
                  <h3 className="text-cyan-200 font-medium">🛠️ AI Order Generation Assistant</h3>
                  <div className="mt-4">
                    <AILightComponent 
                      param1={currentAIAction} 
                      param2={selectedLanguage || "en"} 
                    />
                  </div>
                  <button
                    onClick={() => setShowAIResponse(false)}
                    className="text-xs w-24 bg-red-800 hover:bg-gray-700 px-3 py-1 rounded text-gray-100"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}

            {enableVoiceInput && (
              <div className="mt-6 p-4 border border-purple-700 bg-gray-950 rounded-xl">
                <div className="flex flex-col mb-3">
                  <h3 className="text-cyan-200 font-medium">🛠️ Voice Input Assistant</h3>
                  <div className="mt-4">
                    <SpeechInputComponent
                        label="for Seller"
                        name="voiceInput"
                        value={voiceInput}
                        onChange={(e) => setVoiceInput(e.target.value)}
                        placeholder="Click Mic and Speak or Talk for Voice Input..."
                        maxLength="2000"
                      />
                  </div>
                  <button
                    onClick={() => setShowAIResponse(false)}
                    className="text-xs w-24 bg-red-800 hover:bg-gray-700 px-3 py-1 rounded text-gray-100"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}

            {/* Multi Select Items */}
            <div className="max-h-64 overflow-y-auto border border-gray-700 rounded p-3 space-y-4 bg-gray-950">
              {itemsList.map((item) => {
                const selected = selectedItems[item];

                // Dummy pricing - you can later connect to real API data
                const currentPrice = parseFloat((Math.random() * 80 + 20).toFixed(0));
                const lastPrice = parseFloat((currentPrice + (Math.random() * 20 - 10)).toFixed(0)); // random change
                const unitName = "kg";

                const priceDiff = currentPrice - lastPrice;
                const percentChange = lastPrice !== 0 ? ((priceDiff / lastPrice) * 100).toFixed(1) : 0;

                let arrowIcon = "↔️"; // default same
                let arrowColor = "text-gray-400";

                if (priceDiff < 0) {
                  arrowIcon = "↑";      // Price went down → good for buyer
                  arrowColor = "text-green-400";
                } else if (priceDiff > 0) {
                  arrowIcon = "↓";      // Price went up → bad for buyer
                  arrowColor = "text-red-400";
                }

                return (
                  <div key={item} className="border border-gray-700 rounded-lg p-4 bg-gray-900">
                    {/* Item Header with Pricing + Change Indicator */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={!!selected}
                          onChange={() => toggleItem(item)}
                          className="w-5 h-5 accent-teal-500"
                        />
                        <span className="font-medium text-lg">{item}</span>
                      </div>

                      {/* Price Display with Arrow & % Change */}
                      <div className="text-right">
                        <div className="flex items-center justify-end gap-2 text-sm">
                          <span className="text-teal-400 font-medium">
                            Current: ₹{currentPrice} / {unitName}
                          </span>
                          <span className={`text-lg font-bold ${arrowColor}`}>
                            {arrowIcon}
                          </span>
                          <span className={`text-xs font-medium ${arrowColor}`}>
                            {percentChange > 0 ? "+" : ""}{percentChange}%
                          </span>
                        </div>

                        <div className="text-gray-500 text-sm line-through mt-0.5">
                          Last: ₹{lastPrice} / {unitName}
                        </div>
                        {/* New: Thinky IZC Camera Button */}
                        <button
                          onClick={() => {
                            alert(`Connecting to Thinky IZC Digital Twin Camera for ${item} at ${supplierCode || 'this shop'}...\n\nLive view will open here when camera is connected.`);
                            // Future: Open live camera feed / Digital Twin view
                          }}
                          className="mt-3 w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all active:scale-[0.985]"
                        >
                          📹 Show Live
                        </button>
                      </div>
                    </div>

                    {/* Special Offer Banner - Your previous logic preserved */}
                    {supplierCode && (
                      <OfferSection 
                        item={item} 
                        supplierCode={supplierCode} 
                      />
                    )}

                    {/* Item Configuration (shown only when selected) - Your original logic preserved */}
                    {selected && (
                      <div className="mt-4 grid gap-3 pt-4 border-t border-gray-700">
                        {/* Mode Selection */}
                        <div className="flex gap-2">
                          <button
                            className={`flex-1 px-4 py-2 rounded text-sm font-medium transition ${
                              selected.mode === "amount"
                                ? "bg-white text-black"
                                : "bg-gray-800 hover:bg-gray-700"
                            }`}
                            onClick={() => handleItemChange(item, "mode", "amount")}
                          >
                            By Amount
                          </button>
                          <button
                            className={`flex-1 px-4 py-2 rounded text-sm font-medium transition ${
                              selected.mode === "qty"
                                ? "bg-white text-black"
                                : "bg-gray-800 hover:bg-gray-700"
                            }`}
                            onClick={() => handleItemChange(item, "mode", "qty")}
                          >
                            By Quantity
                          </button>
                        </div>
                        {getError(`item_${item}_mode`)}

                        {/* Amount Input */}
                        {selected.mode === "amount" && (
                          <>
                            <input
                              type="number"
                              placeholder="Enter Amount ₹"
                              className="p-3 bg-gray-900 border border-gray-700 rounded"
                              value={selected.amount || ""}
                              onChange={(e) => handleItemChange(item, "amount", e.target.value)}
                            />
                            {getError(`item_${item}_amount`)}
                          </>
                        )}

                        {/* Qty + Unit */}
                        {selected.mode === "qty" && (
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <input
                                type="number"
                                placeholder="Quantity"
                                className="h-12 p-3 bg-gray-900 border border-gray-700 rounded w-full"
                                value={selected.qty || ""}
                                onChange={(e) => handleItemChange(item, "qty", e.target.value)}
                              />
                              {getError(`item_${item}_qty`)}
                            </div>
                            <div>
                              <select
                                className="h-12 p-3 bg-gray-900 border border-gray-700 rounded w-full"
                                value={selected.unit || ""}
                                onChange={(e) => handleItemChange(item, "unit", e.target.value)}
                              >
                                <option value="">Select Unit</option>
                                {units.map((u) => (
                                  <option key={u} value={u}>
                                    {u}
                                  </option>
                                ))}
                              </select>
                              {getError(`item_${item}_unit`)}
                            </div>
                          </div>
                        )}

                        {/* Instruction */}
                        <input
                          type="text"
                          placeholder="Special Instruction (optional)"
                          className="p-3 bg-gray-900 border border-gray-700 rounded"
                          value={selected.instruction || ""}
                          onChange={(e) => handleItemChange(item, "instruction", e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Final Remarks */}
            <div className="flex flex-col">
              <label className="mb-1">Final Remarks Before Placing Order</label>
              <textarea
                className="p-3 bg-gray-900 border border-gray-700 rounded"
                value={finalRemarks}
                style={{height: "100px"}}
                onChange={(e) => setFinalRemarks(e.target.value)}
                placeholder="Any final instructions..."
              />
            </div>

            {/* Action Buttons */}
            <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-1">
              <button
                onClick={handleSaveDraft}
                className="bg-gray-700 hover:bg-gray-600 text-white py-3.5 rounded-xl font-semibold transition-all active:scale-[0.985] flex items-center justify-center gap-2"
              >
                💾 Save as Draft
              </button>

              <button
                onClick={handlePlaceOrderClick}
                className="bg-orange-500 hover:bg-orange-600 text-black py-3.5 rounded-xl font-semibold transition-all active:scale-[0.985]"
              >
                Place Order to {supplierCode || "<<Supplier>>"}
              </button>

              <button
                onClick={handleSaveDraft}
                disabled={!supplierCode}   // Disabled if supplier code is missing
                className="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-500 disabled:cursor-not-allowed text-white py-3.5 rounded-xl font-semibold transition-all active:scale-[0.985] flex items-center justify-center gap-2 border border-gray-600 disabled:border-gray-700"
              >
                <span className="text-xl">🏠</span>
                Save to Home Management Record
              </button>

              {/* Notify To Group Members */}
              <button
                onClick={handleNotifyGroup}
                disabled={!supplierCode || Object.keys(selectedItems).length === 0}   // Disabled if no supplier or no items
                className="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-500 disabled:cursor-not-allowed text-white py-3.5 rounded-xl font-semibold transition-all active:scale-[0.985] flex items-center justify-center gap-2 border border-gray-600 disabled:border-gray-700"
              >
                <span className="text-xl">👥</span>
                Notify To Group Members
              </button>
            </div>

            {/* Payment & Delivery Method Selector - Shown after clicking Place Order */}
            {showPaymentOptions && (
              <div className="mt-6 p-6 bg-gray-900 border border-gray-700 rounded-2xl">
                <h3 className="text-lg font-semibold mb-5 text-center">Choose Payment & Delivery Method</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  
                  {/* COD - Cash on Delivery */}
                  <button
                    onClick={() => handlePaymentSelect("cod")}
                    className={`p-5 rounded-2xl border-2 transition-all text-left ${selectedPaymentMethod === "cod" ? "border-green-500 bg-green-950/50" : "border-gray-700 hover:border-gray-500"}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="text-3xl">💵</div>
                      <div>
                        <div className="font-semibold text-lg">Cash on Delivery (COD)</div>
                        <div className="text-sm text-gray-400 mt-1">Pay in cash when order is delivered at your location.</div>
                      </div>
                    </div>
                  </button>

                  {/* Pay On Shop & Pickup */}
                  <button
                    onClick={() => handlePaymentSelect("payOnShop")}
                    className={`p-5 rounded-2xl border-2 transition-all text-left ${selectedPaymentMethod === "payOnShop" ? "border-green-500 bg-green-950/50" : "border-gray-700 hover:border-gray-500"}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="text-3xl">🏪</div>
                      <div>
                        <div className="font-semibold text-lg">Pay on Shop + Pickup</div>
                        <div className="text-sm text-gray-400 mt-1">Visit the shop, pay there and pick up your order.</div>
                      </div>
                    </div>
                  </button>

                  {/* Online Payment + Home Delivery */}
                  <button
                    onClick={() => handlePaymentSelect("onlineHome")}
                    className={`p-5 rounded-2xl border-2 transition-all text-left ${selectedPaymentMethod === "onlineHome" ? "border-green-500 bg-green-950/50" : "border-gray-700 hover:border-gray-500"}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="text-3xl">🏠</div>
                      <div>
                        <div className="font-semibold text-lg">Online Payment + Home Delivery</div>
                        <div className="text-sm text-gray-400 mt-1">Pay online now and get delivered at your doorstep.</div>
                      </div>
                    </div>
                  </button>

                  {/* Online Payment + Shop Pickup */}
                  <button
                    onClick={() => handlePaymentSelect("onlinePickup")}
                    className={`p-5 rounded-2xl border-2 transition-all text-left ${selectedPaymentMethod === "onlinePickup" ? "border-green-500 bg-green-950/50" : "border-gray-700 hover:border-gray-500"}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="text-3xl">📦</div>
                      <div>
                        <div className="font-semibold text-lg">Online Payment + Shop Pickup</div>
                        <div className="text-sm text-gray-400 mt-1">Pay online and collect from shop at your convenience.</div>
                      </div>
                    </div>
                  </button>
                </div>

                {/* Confirm Button */}
                <button
                  onClick={handleConfirmOrder}
                  disabled={!selectedPaymentMethod}
                  className={`mt-8 w-full py-4 rounded-2xl font-bold text-lg transition-all ${
                    selectedPaymentMethod 
                      ? "bg-white text-black hover:bg-gray-100" 
                      : "bg-gray-700 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  {selectedPaymentMethod ? "Confirm Order with Selected Method" : "Select a Payment & Delivery Option"}
                </button>

                <button
                  onClick={() => setShowPaymentOptions(false)}
                  className="mt-3 w-full text-gray-400 hover:text-gray-300 text-sm py-2"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}

        <footer className="bg-black border-t border-blue-400 text-white py-4 mt-auto">
          <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
            <div className="text-sm text-yellow-200">
              © 2026 Think5Whys Global DeepTech Solutions.
            </div>
            <div className="flex flex-wrap gap-4 text-sm">
              <Link to="/about-us" className="hover:text-teal-300">About</Link>
              <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
              <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
              <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
            </div>
          </div>
        </footer>
      </div>
    );
};

export default BudgetBasket;